import React, { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { UserAgent, Registerer, RegistererState, Inviter, SessionState } from 'sip.js';
import { Phone, PhoneOff, Mic, MicOff, X, Settings2, Delete, Pause, Play, PhoneForwarded, ParkingCircle, LogOut } from 'lucide-react';
import { toast } from 'sonner';
import { usePhone } from '../../contexts/PhoneContext';
import { useAuth } from '../../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PAD = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#'];

// a=sendonly re-INVITE modifier (hold)
const holdModifier = (description) => {
  let sdp = description.sdp;
  if (!/a=(sendrecv|sendonly|recvonly|inactive)/.test(sdp)) {
    sdp = sdp.replace(/(m=[^\r]*\r\n)/g, '$1a=sendonly\r\n');
  } else {
    sdp = sdp.replace(/a=sendrecv\r\n/g, 'a=sendonly\r\n').replace(/a=recvonly\r\n/g, 'a=inactive\r\n');
  }
  return Promise.resolve({ ...description, sdp });
};

export const WebRTCPhone = () => {
  const { token } = useAuth();
  const { config, setConfig, saveSettings, widgetOpen, setWidgetOpen, pendingNumber, setPendingNumber, deskphoneCall } = usePhone();
  const [number, setNumber] = useState('');
  const [regState, setRegState] = useState('off');
  const [callState, setCallState] = useState('idle'); // idle|calling|active
  const [muted, setMuted] = useState(false);
  const [held, setHeld] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [panel, setPanel] = useState('dial'); // dial|settings|transfer|park
  const [xferTarget, setXferTarget] = useState('');
  const [consultState, setConsultState] = useState('idle'); // idle|calling|active
  const consultRef = useRef(null);
  const [parkOrbit, setParkOrbit] = useState(() => localStorage.getItem('phone.parkOrbit') || '');
  const [loginExt, setLoginExt] = useState('');
  const [loginPw, setLoginPw] = useState('');
  const [loginBusy, setLoginBusy] = useState(false);
  const uaRef = useRef(null);
  const regRef = useRef(null);
  const sessionRef = useRef(null);
  const audioRef = useRef(null);
  const timerRef = useRef(null);

  const webrtc = config?.webrtc;
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (pendingNumber) { setNumber(pendingNumber); setPendingNumber(''); setPanel('dial'); }
  }, [pendingNumber, setPendingNumber]);

  useEffect(() => {
    if (config && !loginExt) {
      setLoginExt(config.extension ? `${config.extension}@${config.default_sip_domain || ''}` : `@${config.default_sip_domain || ''}`);
    }
  }, [config]); // eslint-disable-line react-hooks/exhaustive-deps

  const teardownUA = useCallback(async () => {
    try { if (regRef.current) await regRef.current.unregister(); } catch { /* gone */ }
    try { if (uaRef.current) await uaRef.current.stop(); } catch { /* gone */ }
    uaRef.current = null; regRef.current = null;
    setRegState('off');
  }, []);

  // Register the softphone whenever the widget is open and creds exist.
  useEffect(() => {
    if (!widgetOpen || !webrtc || uaRef.current) return;
    let cancelled = false;
    const ua = new UserAgent({
      uri: UserAgent.makeURI(`sip:${webrtc.username}@${webrtc.sip_domain}`),
      transportOptions: { server: webrtc.wss_url },
      authorizationUsername: webrtc.username,
      authorizationPassword: webrtc.password,
      logLevel: 'error',
      sessionDescriptionHandlerFactoryOptions: { constraints: { audio: true, video: false } },
    });
    uaRef.current = ua;
    setRegState('connecting');
    ua.start()
      .then(() => {
        if (cancelled) return;
        const reg = new Registerer(ua, { expires: 60 });
        regRef.current = reg;
        reg.stateChange.addListener((s) => {
          setRegState(s === RegistererState.Registered ? 'ready' : s === RegistererState.Terminated ? 'error' : 'connecting');
        });
        return reg.register({
          requestDelegate: {
            onReject: () => setRegState('error'),
            onAccept: () => setRegState('ready'),
          },
        });
      })
      .catch(() => { if (!cancelled) setRegState('error'); });
    return () => { cancelled = true; };
  }, [widgetOpen, webrtc]);

  const attachAudio = (session) => {
    const pc = session.sessionDescriptionHandler?.peerConnection;
    if (!pc || !audioRef.current) return;
    const stream = new MediaStream();
    pc.getReceivers().forEach((r) => { if (r.track) stream.addTrack(r.track); });
    audioRef.current.srcObject = stream;
    audioRef.current.play().catch(() => {});
  };

  const endCall = useCallback(() => {
    const c = consultRef.current;
    if (c) {
      try {
        if (c.state === SessionState.Established) c.bye();
        else if (c.state === SessionState.Establishing) c.cancel();
      } catch { /* gone */ }
      consultRef.current = null;
    }
    setConsultState('idle');
    const s = sessionRef.current;
    if (s) {
      try {
        if (s.state === SessionState.Established) s.bye();
        else if (s.state === SessionState.Establishing) s.cancel();
      } catch { /* already ended */ }
    }
    sessionRef.current = null;
    setCallState('idle'); setMuted(false); setHeld(false); setPanel('dial');
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
  }, []);

  const call = () => {
    const dest = number.replace(/[^\d+*#]/g, '');
    if (!dest || !uaRef.current || regState !== 'ready') return;
    const target = UserAgent.makeURI(`sip:${dest}@${webrtc.sip_domain}`);
    const inviter = new Inviter(uaRef.current, target, {
      sessionDescriptionHandlerOptions: { constraints: { audio: true, video: false } },
    });
    sessionRef.current = inviter;
    setCallState('calling'); setSeconds(0);
    inviter.stateChange.addListener((s) => {
      if (s === SessionState.Established) {
        setCallState('active');
        attachAudio(inviter);
        timerRef.current = setInterval(() => setSeconds((v) => v + 1), 1000);
      }
      if (s === SessionState.Terminated) endCall();
    });
    inviter.invite().catch(() => endCall());
  };

  const toggleMute = () => {
    const pc = sessionRef.current?.sessionDescriptionHandler?.peerConnection;
    if (!pc) return;
    const next = !muted;
    pc.getSenders().forEach((s) => { if (s.track) s.track.enabled = !next; });
    setMuted(next);
  };

  const toggleHold = async () => {
    const s = sessionRef.current;
    if (!s || s.state !== SessionState.Established) return;
    const next = !held;
    try {
      await s.invite({ sessionDescriptionHandlerModifiers: next ? [holdModifier] : [] });
      const pc = s.sessionDescriptionHandler?.peerConnection;
      if (pc) pc.getSenders().forEach((sn) => { if (sn.track) sn.track.enabled = !next && !muted; });
      setHeld(next);
      toast.message(next ? 'Call on hold' : 'Call resumed');
    } catch {
      toast.error('Hold failed');
    }
  };

  const blindTransfer = async (destRaw) => {
    const dest = String(destRaw || '').replace(/[^\d+*#]/g, '');
    const s = sessionRef.current;
    if (!dest || !s || s.state !== SessionState.Established) return;
    try {
      await s.refer(UserAgent.makeURI(`sip:${dest}@${webrtc.sip_domain}`));
      toast.success(`Transferred to ${dest}`);
      endCall();
    } catch {
      toast.error('Transfer failed');
    }
  };

  const parkCall = async () => {
    if (!parkOrbit) { toast.error('Enter a park orbit first'); return; }
    localStorage.setItem('phone.parkOrbit', parkOrbit);
    await blindTransfer(parkOrbit);
  };

  // Attended transfer: hold the caller, consult the target, then hand over.
  const startConsult = async (destRaw) => {
    const dest = String(destRaw || '').replace(/[^\d+*#]/g, '');
    const s = sessionRef.current;
    if (!dest || !s || s.state !== SessionState.Established) return;
    if (!held) await toggleHold();
    const inviter = new Inviter(uaRef.current, UserAgent.makeURI(`sip:${dest}@${webrtc.sip_domain}`), {
      sessionDescriptionHandlerOptions: { constraints: { audio: true, video: false } },
    });
    consultRef.current = inviter;
    setConsultState('calling');
    setPanel('dial');
    inviter.stateChange.addListener((st) => {
      if (st === SessionState.Established) {
        setConsultState('active');
        attachAudio(inviter);
      }
      if (st === SessionState.Terminated && consultRef.current === inviter) {
        consultRef.current = null;
        setConsultState('idle');
        if (sessionRef.current) attachAudio(sessionRef.current);
      }
    });
    inviter.invite().catch(() => {
      consultRef.current = null;
      setConsultState('idle');
      toast.error('Consult call failed');
    });
  };

  const completeAttended = async () => {
    const s = sessionRef.current;
    const c = consultRef.current;
    if (!s || !c) return;
    try {
      await s.refer(c);
      toast.success('Transfer completed');
      consultRef.current = null;
      setConsultState('idle');
      endCall();
    } catch {
      toast.error('Transfer failed');
    }
  };

  const cancelConsult = async () => {
    const c = consultRef.current;
    consultRef.current = null;
    setConsultState('idle');
    if (c) {
      try {
        if (c.state === SessionState.Established) c.bye();
        else if (c.state === SessionState.Establishing) c.cancel();
      } catch { /* gone */ }
    }
    if (held) await toggleHold();
    if (sessionRef.current) attachAudio(sessionRef.current);
  };

  const sendDtmf = (tone) => {
    const s = consultRef.current && consultState === 'active' ? consultRef.current : sessionRef.current;
    try { s?.sessionDescriptionHandler?.sendDtmf(tone); } catch { /* unsupported */ }
  };

  const padPress = (k) => {
    if (callState === 'active') sendDtmf(k);
    else setNumber((n) => n + k);
  };

  const sipLogin = async () => {
    if (!loginExt || !loginPw) return;
    setLoginBusy(true);
    try {
      const r = await axios.post(`${API}/phone/login`, { extension: loginExt, password: loginPw }, { headers });
      await teardownUA();
      setConfig((c) => ({
        ...c, webrtc_error: null,
        webrtc: { wss_url: r.data.wss_url, sip_domain: r.data.sip_domain, username: r.data.username, password: loginPw },
      }));
      setLoginPw('');
      toast.success(`Signing in ${r.data.username}@${r.data.sip_domain}…`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Phone sign-in failed');
    } finally {
      setLoginBusy(false);
    }
  };

  const sipLogout = async () => {
    endCall();
    await teardownUA();
    try { await axios.post(`${API}/phone/logout`, {}, { headers }); } catch { /* best effort */ }
    setConfig((c) => ({ ...c, webrtc: null, webrtc_error: 'not_logged_in' }));
    setPanel('dial');
  };

  if (!config || !config.phone_enabled) return null;
  const mmss = `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;
  const regDot = regState === 'ready' ? 'bg-emerald-500' : regState === 'connecting' ? 'bg-amber-400 animate-pulse' : regState === 'error' ? 'bg-red-500' : 'bg-slate-300';
  const regLabel = !webrtc ? 'Sign in to your extension'
    : regState === 'ready' ? `${webrtc.username}@${webrtc.sip_domain}`
    : regState === 'connecting' ? 'Connecting…'
    : regState === 'error' ? 'Registration failed — check password'
    : 'Offline';

  const inCall = callState !== 'idle';

  return (
    <>
      <audio ref={audioRef} autoPlay style={{ display: 'none' }} />
      {!widgetOpen && (
        <button onClick={() => setWidgetOpen(true)}
          className="fixed bottom-5 right-5 z-[80] w-12 h-12 rounded-full bg-teal-600 hover:bg-teal-700 text-white shadow-lg flex items-center justify-center transition-transform hover:scale-105"
          data-testid="phone-widget-launcher" title="Open phone">
          <Phone className="w-5 h-5" />
        </button>
      )}
      {widgetOpen && (
        <div className="fixed bottom-5 right-5 z-[80] w-72 bg-white border border-slate-200 rounded-2xl shadow-2xl overflow-hidden" data-testid="phone-widget">
          <div className="flex items-center justify-between px-4 py-2.5 bg-slate-900 text-white">
            <div className="flex items-center gap-2 text-xs font-semibold truncate">
              <span className={`w-2 h-2 rounded-full shrink-0 ${regDot}`} data-testid="phone-reg-dot" />
              <span className="truncate" data-testid="phone-reg-label">{regLabel}</span>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <button onClick={() => setPanel(panel === 'settings' ? 'dial' : 'settings')} className="p-1 rounded hover:bg-white/10" data-testid="phone-settings-btn"><Settings2 className="w-3.5 h-3.5" /></button>
              <button onClick={() => setWidgetOpen(false)} className="p-1 rounded hover:bg-white/10" data-testid="phone-widget-close"><X className="w-3.5 h-3.5" /></button>
            </div>
          </div>

          {!webrtc && panel !== 'settings' ? (
            <div className="p-4 space-y-2.5" data-testid="phone-login-panel">
              <p className="text-[11px] text-slate-500">Use your Yabbit extension and its SIP password.</p>
              <input value={loginExt} onChange={(e) => setLoginExt(e.target.value)} placeholder="extension@domain"
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:border-teal-400"
                data-testid="phone-login-extension" />
              <input type="password" value={loginPw} onChange={(e) => setLoginPw(e.target.value)} placeholder="Extension password"
                onKeyDown={(e) => e.key === 'Enter' && sipLogin()}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-teal-400"
                data-testid="phone-login-password" />
              <button onClick={sipLogin} disabled={loginBusy || !loginExt || !loginPw}
                className="w-full py-2 rounded-xl bg-teal-600 hover:bg-teal-700 disabled:opacity-40 text-white text-sm font-semibold"
                data-testid="phone-login-btn">{loginBusy ? 'Connecting…' : 'Connect phone'}</button>
            </div>
          ) : panel === 'settings' ? (
            <div className="p-4 space-y-3 text-xs" data-testid="phone-settings-panel">
              <div>
                <label className="font-semibold text-slate-700 block mb-1">Desktop calls use</label>
                <select value={config.call_mode} onChange={(e) => saveSettings({ call_mode: e.target.value })}
                  className="w-full border border-slate-200 rounded-lg px-2 py-1.5" data-testid="phone-call-mode">
                  <option value="webrtc">Browser headset (webphone)</option>
                  <option value="deskphone" disabled={!config.deskphone_available}>Ring my desk phone first</option>
                </select>
              </div>
              <div>
                <label className="font-semibold text-slate-700 block mb-1">On mobile, open</label>
                <select value={config.mobile_mode} onChange={(e) => saveSettings({ mobile_mode: e.target.value })}
                  className="w-full border border-slate-200 rounded-lg px-2 py-1.5" data-testid="phone-mobile-mode">
                  <option value="yabbit_app">Yabbit mobile app</option>
                  <option value="dialer">Phone's normal dialer</option>
                  <option value="ask">Ask me each time</option>
                </select>
              </div>
              <div>
                <label className="font-semibold text-slate-700 block mb-1">Park orbit</label>
                <input value={parkOrbit} onChange={(e) => { setParkOrbit(e.target.value); localStorage.setItem('phone.parkOrbit', e.target.value); }}
                  placeholder="e.g. 800" className="w-full border border-slate-200 rounded-lg px-2 py-1.5 font-mono" data-testid="phone-park-orbit" />
              </div>
              {webrtc && (
                <button onClick={sipLogout} className="w-full py-1.5 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 font-semibold flex items-center justify-center gap-1.5" data-testid="phone-sip-logout">
                  <LogOut className="w-3.5 h-3.5" /> Sign out of {webrtc.username}
                </button>
              )}
              <button onClick={() => setPanel('dial')} className="w-full py-1.5 rounded-lg bg-slate-900 text-white font-semibold" data-testid="phone-settings-done">Done</button>
            </div>
          ) : panel === 'transfer' ? (
            <div className="p-4 space-y-2.5" data-testid="phone-transfer-panel">
              <p className="text-xs font-semibold text-slate-700">Transfer call to</p>
              <input value={xferTarget} onChange={(e) => setXferTarget(e.target.value)} placeholder="Extension or number"
                onKeyDown={(e) => e.key === 'Enter' && blindTransfer(xferTarget)}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:border-teal-400"
                data-testid="phone-transfer-input" autoFocus />
              <div className="flex gap-2">
                <button onClick={() => setPanel('dial')} className="py-2 px-3 rounded-xl border border-slate-200 text-slate-600 text-sm font-medium" data-testid="phone-transfer-cancel">Back</button>
                <button onClick={() => startConsult(xferTarget)} disabled={!xferTarget}
                  className="flex-1 py-2 rounded-xl border border-indigo-300 text-indigo-700 hover:bg-indigo-50 disabled:opacity-40 text-sm font-semibold"
                  title="Speak to them first, then complete the transfer"
                  data-testid="phone-transfer-attended">Call first</button>
                <button onClick={() => blindTransfer(xferTarget)} disabled={!xferTarget}
                  className="flex-1 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40 text-white text-sm font-semibold"
                  data-testid="phone-transfer-confirm">Transfer now</button>
              </div>
            </div>
          ) : (
            <div className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <input value={number} onChange={(e) => setNumber(e.target.value)}
                  placeholder="Number…" inputMode="tel" disabled={inCall}
                  className="flex-1 border border-slate-200 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:border-teal-400 disabled:bg-slate-50"
                  data-testid="phone-number-input" />
                <button onClick={() => setNumber((n) => n.slice(0, -1))} disabled={inCall} className="p-2 text-slate-400 hover:text-slate-600 disabled:opacity-30" data-testid="phone-backspace"><Delete className="w-4 h-4" /></button>
              </div>
              <div className="grid grid-cols-3 gap-1.5 mb-3">
                {PAD.map((k) => (
                  <button key={k} onClick={() => padPress(k)}
                    className="py-2 rounded-lg bg-slate-50 hover:bg-slate-100 border border-slate-200 text-sm font-semibold text-slate-700"
                    data-testid={`phone-pad-${k === '*' ? 'star' : k === '#' ? 'hash' : k}`}>{k}</button>
                ))}
              </div>
              {callState === 'idle' ? (
                <div className="space-y-1.5">
                  <button onClick={call} disabled={!number || regState !== 'ready'}
                    className="w-full py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white font-semibold flex items-center justify-center gap-2"
                    data-testid="phone-call-btn">
                    <Phone className="w-4 h-4" /> Call
                  </button>
                  {config.deskphone_available && (
                    <button onClick={() => number && deskphoneCall(number)}
                      className="w-full py-1.5 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50 text-xs font-medium"
                      data-testid="phone-deskphone-btn">Ring my desk phone instead</button>
                  )}
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="text-center text-xs font-semibold text-slate-600" data-testid="phone-call-status">
                    {callState === 'calling' ? `Calling ${number}…` : `${held ? 'On hold' : 'In call'} · ${mmss}`}
                  </div>
                  {consultState !== 'idle' && (
                    <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-2 space-y-1.5" data-testid="phone-consult-bar">
                      <div className="text-[11px] font-semibold text-indigo-700 text-center">
                        {consultState === 'calling' ? `Calling ${xferTarget}…` : `Consulting ${xferTarget}`}
                      </div>
                      <div className="flex gap-1.5">
                        <button onClick={cancelConsult}
                          className="flex-1 py-1.5 rounded-lg border border-indigo-200 text-indigo-600 text-[11px] font-semibold hover:bg-white"
                          data-testid="phone-consult-cancel">Cancel · back to caller</button>
                        <button onClick={completeAttended} disabled={consultState !== 'active'}
                          className="flex-1 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40 text-white text-[11px] font-semibold"
                          data-testid="phone-consult-complete">Complete transfer</button>
                      </div>
                    </div>
                  )}
                  <div className="grid grid-cols-4 gap-1.5">
                    <button onClick={toggleMute} title={muted ? 'Unmute' : 'Mute'}
                      className={`py-2 rounded-lg border flex items-center justify-center ${muted ? 'bg-amber-50 border-amber-300 text-amber-600' : 'border-slate-200 text-slate-500 hover:bg-slate-50'}`}
                      data-testid="phone-mute-btn">
                      {muted ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                    </button>
                    <button onClick={toggleHold} disabled={callState !== 'active'} title={held ? 'Resume' : 'Hold'}
                      className={`py-2 rounded-lg border flex items-center justify-center disabled:opacity-30 ${held ? 'bg-sky-50 border-sky-300 text-sky-600' : 'border-slate-200 text-slate-500 hover:bg-slate-50'}`}
                      data-testid="phone-hold-btn">
                      {held ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
                    </button>
                    <button onClick={parkCall} disabled={callState !== 'active'} title={`Park on orbit ${parkOrbit || '—'}`}
                      className="py-2 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 flex items-center justify-center disabled:opacity-30"
                      data-testid="phone-park-btn">
                      <ParkingCircle className="w-4 h-4" />
                    </button>
                    <button onClick={() => setPanel('transfer')} disabled={callState !== 'active'} title="Transfer"
                      className="py-2 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 flex items-center justify-center disabled:opacity-30"
                      data-testid="phone-transfer-btn">
                      <PhoneForwarded className="w-4 h-4" />
                    </button>
                  </div>
                  <button onClick={endCall}
                    className="w-full py-2.5 rounded-xl bg-red-600 hover:bg-red-700 text-white font-semibold flex items-center justify-center gap-2"
                    data-testid="phone-hangup-btn">
                    <PhoneOff className="w-4 h-4" /> {callState === 'calling' ? 'Cancel' : 'End call'}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
};
